import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, XCircle } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";
import { useState } from "react";

export default function AuthCallback() {
  const { exchangeGoogleSession } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const done = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    // Emergent returns session_id in the URL fragment
    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    // Optional `next` from query string (?next=/portal)
    const q = new URLSearchParams(location.search);
    const next = q.get("next") || "/portal";
    if (!sid) {
      setError("Missing session token from Google sign-in.");
      return;
    }
    exchangeGoogleSession(sid)
      .then((u) => {
        // Clean URL fragment
        window.history.replaceState(null, "", window.location.pathname);
        nav(u?.role === "admin" ? "/admin" : next, { replace: true });
      })
      .catch((err) => setError(formatApiError(err)));
  }, [exchangeGoogleSession, nav, location.search]);

  return (
    <div className="min-h-screen grid place-items-center bg-white px-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center max-w-sm">
        {error ? (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-rose-50 grid place-items-center mb-4">
              <XCircle className="w-6 h-6 text-rose-600" />
            </div>
            <h1 className="font-display text-2xl font-semibold">Sign-in failed</h1>
            <p className="text-sm text-gray-500 mt-2" data-testid="auth-callback-error">{error}</p>
            <button onClick={() => nav("/login", { replace: true })} className="mt-6 text-sm font-medium text-[#4F46E5] hover:underline">
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[#4F46E5] animate-spin mb-3" />
            <div className="font-display text-lg font-semibold">Finishing sign in…</div>
            <p className="text-xs text-gray-500 mt-1">Bridging your Google session.</p>
          </>
        )}
      </motion.div>
    </div>
  );
}
